import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import axios from "axios";
import { SERVER } from "../../../../Constants";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Divider,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

const SupportAgentComponent = ({ setActiveComponent }) => {
  const theme = useTheme();
  const { currentUser, chatClient } = useAuth();
  const [allChats, setAllChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [takingOver, setTakingOver] = useState(false);
  const [closingChat, setClosingChat] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open"); // 'open', 'closed', 'all'
  const [handlerFilter, setHandlerFilter] = useState("all"); // 'ai', 'agent', 'all'

  // Fetch all chats with server-side filtering
  const fetchAllChats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters based on filters
      const params = new URLSearchParams();
      
      // Status filter
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      
      // Handler filter
      if (handlerFilter === "ai") {
        params.append("aiHandling", "true");
      } else if (handlerFilter === "agent") {
        params.append("liveAgent", "true");
      }
      
      const queryString = params.toString();
      const url = `${SERVER}/support/all-chats${queryString ? `?${queryString}` : ""}`;
      
      const response = await axios.get(url, {
        withCredentials: true,
      });
      setAllChats(response.data.chats || []);
    } catch (err) {
      console.error("Error fetching chats:", err);
      setError(err.response?.data?.message || "Failed to fetch chats");
    } finally {
      setLoading(false);
    }
  };

  // Fetch chats when component mounts or filters change
  useEffect(() => {
    if (currentUser) {
      fetchAllChats();
    }
  }, [currentUser, statusFilter, handlerFilter]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (currentUser) {
      const interval = setInterval(fetchAllChats, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUser, statusFilter, handlerFilter]);

  // Handle agent taking over a chat
  const handleTakeOver = async (chat) => {
    if (!currentUser) return;

    try {
      setTakingOver(true);
      setError(null);

      // Request to join as agent
      const response = await axios.post(
        `${SERVER}/support/agent-join`,
        {
          supportId: chat._id,
          agentName: currentUser.username || currentUser.fullname || "Agent",
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.success) {
        // Redirect to chat component
        console.log("Takeover successful, redirecting to chats...");
        if (setActiveComponent) {
          setActiveComponent("chats");
        } else {
          console.error("setActiveComponent is not available");
        }
      } else {
        console.warn("Takeover response did not indicate success:", response.data);
      }
    } catch (err) {
      console.error("Error taking over chat:", err);
      setError(
        err.response?.data?.message || "Failed to take over the chat"
      );
    } finally {
      setTakingOver(false);
    }
  };

  // Handle closing a chat
  const handleCloseChat = async (chat) => {
    if (!currentUser) return;

    try {
      setClosingChat(chat._id);
      setError(null);

      const response = await axios.post(
        `${SERVER}/support/close`,
        {
          supportId: chat._id,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.message) {
        setSuccess("Chat closed successfully");
        // Refresh the chat list
        await fetchAllChats();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error("Error closing chat:", err);
      setError(
        err.response?.data?.message || "Failed to close the chat"
      );
    } finally {
      setClosingChat(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (!currentUser || !chatClient) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress color="primary" size={40} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading support panel...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 4,
        px: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SupportAgentIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: "bold",
              color: theme.palette.text.primary,
            }}
          >
            Support Queue
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label={`${allChats.length} Chats`}
            color="primary"
            sx={{ fontWeight: "bold" }}
          />
          <Button
            variant="outlined"
            size="medium"
            onClick={fetchAllChats}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "medium",
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* Filters */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
        {/* Status Filter */}
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} p={0} m={0}>
            Status
          </Typography>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(e, newFilter) => newFilter && setStatusFilter(newFilter)}
            size="small"
            sx={{ bgcolor: "background.paper" }}
          >
            <ToggleButton value="open" sx={{ px: 2, textTransform: "none" }}>
              <RadioButtonUncheckedIcon sx={{ mr: 1, fontSize: 18 }} />
              Open
            </ToggleButton>
            <ToggleButton value="closed" sx={{ px: 2, textTransform: "none" }}>
              <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} />
              Closed
            </ToggleButton>
            <ToggleButton value="all" sx={{ px: 2, textTransform: "none" }}>
              All
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Handler Filter */}
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} p={0} m={0}>
            Handler
          </Typography>
          <ToggleButtonGroup
            value={handlerFilter}
            exclusive
            onChange={(e, newFilter) => newFilter && setHandlerFilter(newFilter)}
            size="small"
            sx={{ bgcolor: "background.paper" }}
          >
            <ToggleButton value="ai" sx={{ px: 2, textTransform: "none" }}>
              <SmartToyIcon sx={{ mr: 1, fontSize: 18 }} />
              AI
            </ToggleButton>
            <ToggleButton value="agent" sx={{ px: 2, textTransform: "none" }}>
              <SupportAgentIcon sx={{ mr: 1, fontSize: 18 }} />
              Agent
            </ToggleButton>
            <ToggleButton value="all" sx={{ px: 2, textTransform: "none" }}>
              All
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Stack>

      {/* Success Alert */}
      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }} 
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 2,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, #252018, #1d1915)"
              : "linear-gradient(180deg, #ffffff, #fafafa)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 4px 15px rgba(0,0,0,0.5)"
              : "0 4px 15px rgba(0,0,0,0.1)",
        }}
      >
        {loading && allChats.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress size={50} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Loading chats...
            </Typography>
          </Box>
        ) : allChats.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <SmartToyIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No chats found
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              Try adjusting your filters to see more results
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {allChats.map((chat) => (
              <Grid item xs={12} sm={6} lg={4} key={chat._id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: chat.status === "closed" ? "divider" : "divider",
                    transition: "all 0.3s ease",
                    opacity: chat.status === "closed" ? 0.7 : 1,
                    "&:hover": {
                      borderColor: "primary.main",
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                      opacity: 1,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack spacing={2}>
                      {/* User Name */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PersonIcon sx={{ color: "primary.main", fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }} p={0} m={0}>
                          {chat.user?.fullname || "Unknown User"}
                        </Typography>
                      </Box>

                      {/* Email */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <EmailIcon sx={{ color: "text.secondary", fontSize: 16 }} />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                            p={0}
                            m={0}
                        >
                          {chat.user?.email || "No email"}
                        </Typography>
                      </Box>

                      {/* Time */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <AccessTimeIcon sx={{ color: "text.secondary", fontSize: 16 }} />
                        <Typography variant="body2" color="text.secondary" p={0} m={0}>
                          Started {formatDate(chat.createdAt)}
                        </Typography>
                      </Box>

                      {/* Status Chips */}
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                        {chat.status === "closed" && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Closed"
                            color="default"
                            size="small"
                          />
                        )}
                        {chat.status !== "closed" && chat.isAIHandling && (
                          <Chip
                            icon={<SmartToyIcon />}
                            label="AI Handling"
                            color="info"
                            size="small"
                          />
                        )}
                        {chat.status !== "closed" && chat.liveAgentJoined && (
                          <Chip
                            icon={<SupportAgentIcon />}
                            label="Agent Active"
                            color="success"
                            size="small"
                          />
                        )}
                        {chat.status !== "closed" && !chat.isAIHandling && !chat.liveAgentJoined && (
                          <Chip
                            label="Waiting"
                            color="warning"
                            size="small"
                          />
                        )}
                      </Stack>

                      {/* Close time if closed */}
                      {chat.closedAt && (
                        <Typography variant="caption" color="text.disabled">
                          Closed {formatDate(chat.closedAt)}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                      {chat.status !== "closed" && !chat.liveAgentJoined && (
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          onClick={() => handleTakeOver(chat)}
                          disabled={takingOver}
                          startIcon={
                            takingOver ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <SupportAgentIcon />
                            )
                          }
                          sx={{
                            borderRadius: 1.5,
                            py: 1.2,
                            textTransform: "none",
                            fontWeight: "bold",
                            fontSize: "0.95rem",
                          }}
                        >
                          Take Over
                        </Button>
                      )}
                      {chat.status !== "closed" && (
                        <Button
                          fullWidth={chat.liveAgentJoined}
                          variant="outlined"
                          color="error"
                          onClick={() => handleCloseChat(chat)}
                          disabled={closingChat === chat._id}
                          startIcon={
                            closingChat === chat._id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <CloseIcon />
                            )
                          }
                          sx={{
                            borderRadius: 1.5,
                            py: 1.2,
                            textTransform: "none",
                            fontWeight: "bold",
                            fontSize: "0.95rem",
                          }}
                        >
                          Close
                        </Button>
                      )}
                      {chat.status === "closed" && (
                        <Button
                          fullWidth
                          variant="outlined"
                          disabled
                          sx={{
                            borderRadius: 1.5,
                            py: 1.2,
                            textTransform: "none",
                            fontWeight: "bold",
                            fontSize: "0.95rem",
                          }}
                        >
                          Chat Closed
                        </Button>
                      )}
                    </Stack>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default SupportAgentComponent;
